import { useEffect, useState, type FormEvent } from "react";
import { api, message } from "../api";

interface LinkInfo { email: string; name: string; purpose: "invite" | "reset"; expiresAt: string }

/**
 * Where an invite or reset link lands. The token is in the URL fragment,
 * which the browser never sends to a server; it is read here and posted.
 */
export function SetPassword() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid(true); return; }
    api.post<LinkInfo>("/auth/password-link/inspect", { token })
      .then(setInfo)
      .catch(() => setInvalid(true));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError("The two passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-link/accept", { token, password });
      // The link is spent; keep it out of the address bar and history.
      window.history.replaceState(null, "", "/set-password");
      setDone(true);
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <main className="auth">
      <section className="auth__brand">
        <img className="auth__wordmark" src="/brand/logo-wordmark.png" alt="TimeSaver Offsite" />
        <p className="auth__tagline">Meeting minutes, drafted from the recording.</p>
      </section>
      <section className="auth__panel">
        <div className="auth__form">
          {invalid ? (
            <>
              <h1 className="auth__heading">This link has expired</h1>
              <p className="auth__sub">
                Links work once and last 72 hours. Ask an administrator for a new one.
              </p>
              <a className="btn" href="/">Go to sign in</a>
            </>
          ) : done ? (
            <>
              <h1 className="auth__heading">Password set</h1>
              <p className="auth__sub">
                Sign in with it now. Then add a passkey from your Account page — it
                protects your account even if the password leaks.
              </p>
              <a className="btn" href="/">Sign in</a>
            </>
          ) : !info ? (
            <p className="muted">Checking your link…</p>
          ) : (
            <form onSubmit={submit} noValidate>
              <h1 className="auth__heading">
                {info.purpose === "invite" ? `Welcome, ${info.name}` : "Choose a new password"}
              </h1>
              <p className="auth__sub">
                Set a password for <strong>{info.email}</strong>. At least 12 characters;
                a few unrelated words make a strong one.
              </p>
              {error && <div className="alert" role="alert">{error}</div>}
              {/* Lets a password manager file the new password under the right account. */}
              <input type="email" autoComplete="username" value={info.email} readOnly hidden />
              <div className="field">
                <label htmlFor="newpw">New password</label>
                <input id="newpw" type="password" autoComplete="new-password" required minLength={12}
                       value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="confirmpw">Confirm it</label>
                <input id="confirmpw" type="password" autoComplete="new-password" required
                       value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <button className="btn" type="submit" disabled={busy}>
                {busy ? "Saving…" : "Set password"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
